import { useState, useMemo } from 'react'
import { useSales } from '../hooks/useSales'
import { useExpenses } from '../hooks/useExpenses'
import { usePrints } from '../hooks/usePrints'
import { useMaintenanceEvents } from '../hooks/useMaintenance'
import { useQueueItems } from '../hooks/useQueueItems'

interface CalEvent {
  type: 'print' | 'sale' | 'expense' | 'due' | 'maintenance'
  label: string
  id: string
}

const EVENT_COLORS: Record<CalEvent['type'], string> = {
  print: '#3b82f6',
  sale: '#22c55e',
  expense: '#f97316',
  due: '#ef4444',
  maintenance: '#a855f7',
}

const EVENT_LABELS: Record<CalEvent['type'], string> = {
  print: 'Print',
  sale: 'Sale',
  expense: 'Expense',
  due: 'Due date',
  maintenance: 'Maintenance',
}

export default function Calendar() {
  const today = new Date()
  const [viewYear, setViewYear] = useState(today.getFullYear())
  const [viewMonth, setViewMonth] = useState(today.getMonth()) // 0-based
  const [selectedDay, setSelectedDay] = useState<number | null>(null)

  const { data: sales } = useSales()
  const { data: expenses } = useExpenses()
  const { data: prints } = usePrints()
  const { data: maintenanceEvents } = useMaintenanceEvents()
  const { data: queueItems } = useQueueItems()

  // Build event map: dayNumber → CalEvent[]
  const eventsByDay = useMemo(() => {
    const map = new Map<number, CalEvent[]>()

    const addEvent = (dateStr: string | null | undefined, event: CalEvent) => {
      if (!dateStr) return
      const d = new Date(dateStr + 'T00:00:00')
      if (d.getFullYear() !== viewYear || d.getMonth() !== viewMonth) return
      const day = d.getDate()
      if (!map.has(day)) map.set(day, [])
      map.get(day)!.push(event)
    }

    for (const p of prints ?? []) {
      addEvent(p.print_date, { type: 'print', label: p.description ?? p.purpose, id: p.id })
    }
    for (const s of sales ?? []) {
      addEvent(s.sale_date, { type: 'sale', label: `${s.platform}${s.customer_name ? ' · ' + s.customer_name : ''}`, id: s.id })
    }
    for (const e of expenses ?? []) {
      addEvent(e.expense_date, { type: 'expense', label: e.description ?? e.category, id: e.id })
    }
    for (const me of maintenanceEvents ?? []) {
      addEvent(me.event_date, { type: 'maintenance', label: me.task_type, id: me.id })
    }
    for (const q of queueItems ?? []) {
      if (q.due_date) {
        addEvent(q.due_date, { type: 'due', label: q.title, id: q.id })
      }
    }

    return map
  }, [sales, expenses, prints, maintenanceEvents, queueItems, viewYear, viewMonth])

  const monthName = new Date(viewYear, viewMonth, 1).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  })

  // Grid days
  const firstDayOfMonth = new Date(viewYear, viewMonth, 1).getDay() // 0=Sun
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate()
  const cells: Array<number | null> = [
    ...Array<null>(firstDayOfMonth).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ]
  // pad to complete last row
  while (cells.length % 7 !== 0) cells.push(null)

  const prevMonth = () => {
    if (viewMonth === 0) { setViewYear(viewYear - 1); setViewMonth(11) }
    else setViewMonth(viewMonth - 1)
    setSelectedDay(null)
  }
  const nextMonth = () => {
    if (viewMonth === 11) { setViewYear(viewYear + 1); setViewMonth(0) }
    else setViewMonth(viewMonth + 1)
    setSelectedDay(null)
  }

  const todayDay = today.getFullYear() === viewYear && today.getMonth() === viewMonth
    ? today.getDate()
    : null

  const selectedEvents = selectedDay ? (eventsByDay.get(selectedDay) ?? []) : []

  // Dedupe dots by type per day
  const getDotsForDay = (day: number) => {
    const events = eventsByDay.get(day) ?? []
    const seen = new Set<CalEvent['type']>()
    return events.filter((e) => {
      if (seen.has(e.type)) return false
      seen.add(e.type)
      return true
    })
  }

  return (
    <div style={{ padding: 'var(--space-6)', maxWidth: 640 }}>
      <h1 style={{ marginBottom: 'var(--space-4)' }}>Calendar</h1>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 'var(--space-4)', flexWrap: 'wrap', marginBottom: 'var(--space-4)' }}>
        {(Object.entries(EVENT_LABELS) as Array<[CalEvent['type'], string]>).map(([type, label]) => (
          <div key={type} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 'var(--font-size-xs)' }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: EVENT_COLORS[type], display: 'inline-block' }} />
            <span style={{ color: 'var(--color-text-muted)' }}>{label}</span>
          </div>
        ))}
      </div>

      {/* Navigation */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
        <button
          onClick={prevMonth}
          style={{ padding: 'var(--space-2) var(--space-3)', background: 'var(--color-surface)', borderRadius: 'var(--radius-md)', fontWeight: 600 }}
        >
          ‹
        </button>
        <span style={{ fontWeight: 700, fontSize: 'var(--font-size-lg)' }}>{monthName}</span>
        <button
          onClick={nextMonth}
          style={{ padding: 'var(--space-2) var(--space-3)', background: 'var(--color-surface)', borderRadius: 'var(--radius-md)', fontWeight: 600 }}
        >
          ›
        </button>
      </div>

      {/* Day headers */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 4 }}>
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
          <div key={d} style={{ textAlign: 'center', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)', paddingBottom: 'var(--space-1)' }}>
            {d}
          </div>
        ))}
      </div>

      {/* Day cells */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
        {cells.map((day, idx) => {
          if (day === null) {
            return <div key={`empty-${idx}`} style={{ height: 56 }} />
          }
          const isToday = day === todayDay
          const isSelected = day === selectedDay
          const dots = getDotsForDay(day)

          return (
            <button
              key={day}
              onClick={() => setSelectedDay(selectedDay === day ? null : day)}
              style={{
                height: 56,
                borderRadius: 'var(--radius-md)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 3,
                background: isSelected
                  ? 'var(--color-primary)'
                  : isToday
                    ? 'var(--color-surface-2)'
                    : 'var(--color-surface)',
                border: isToday ? '2px solid var(--color-primary)' : '1px solid var(--color-border)',
                cursor: 'pointer',
              }}
            >
              <span style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: isToday ? 700 : 400,
                color: isSelected ? 'white' : isToday ? 'var(--color-primary)' : 'var(--color-text)',
              }}>
                {day}
              </span>
              {dots.length > 0 && (
                <div style={{ display: 'flex', gap: 2 }}>
                  {dots.slice(0, 4).map((e) => (
                    <span
                      key={e.type}
                      style={{
                        width: 5,
                        height: 5,
                        borderRadius: '50%',
                        background: isSelected ? 'white' : EVENT_COLORS[e.type],
                        display: 'inline-block',
                      }}
                    />
                  ))}
                </div>
              )}
            </button>
          )
        })}
      </div>

      {/* Day detail sheet */}
      {selectedDay !== null && (
        <div style={{
          marginTop: 'var(--space-4)',
          background: 'var(--color-surface)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--space-4)',
        }}>
          <h3 style={{ fontSize: 'var(--font-size-sm)', fontWeight: 700, marginBottom: 'var(--space-3)' }}>
            {new Date(viewYear, viewMonth, selectedDay).toLocaleDateString('en-US', {
              weekday: 'long', month: 'long', day: 'numeric',
            })}
          </h3>
          {selectedEvents.length === 0 ? (
            <p style={{ color: 'var(--color-text-faint)', fontSize: 'var(--font-size-sm)' }}>
              Nothing logged for this day.
            </p>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {selectedEvents.map((ev, i) => (
                <li key={i} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  padding: 'var(--space-2) 0',
                  borderBottom: i < selectedEvents.length - 1 ? '1px solid var(--color-surface-2)' : 'none',
                  fontSize: 'var(--font-size-sm)',
                }}>
                  <span style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: EVENT_COLORS[ev.type],
                    flexShrink: 0,
                  }} />
                  <span style={{ color: 'var(--color-text-faint)', minWidth: 72, fontSize: 'var(--font-size-xs)' }}>
                    {EVENT_LABELS[ev.type]}
                  </span>
                  <span>{ev.label}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
